// Security & System Settings Tab
import React from "react";
import SettingsSaveButtons from "./SettingsSaveButtons";

const SecuritySettings = ({
  settings,
  updateSettings,
  saveSettings,
  saving,
  hasUnsavedChanges,
  resetSettings,
}) => {
  const clampInteger = (value, fallback, min, max) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, Math.floor(parsed)));
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();

    const currentSecuritySettings = settings.securitySettings || {};
    const securityPayload = {
      maxLoginAttempts: clampInteger(
        currentSecuritySettings.maxLoginAttempts,
        5,
        3,
        10,
      ),
      lockoutDurationMinutes: clampInteger(
        currentSecuritySettings.lockoutDurationMinutes,
        15,
        5,
        60,
      ),
      otpExpiryMinutes: clampInteger(
        currentSecuritySettings.otpExpiryMinutes,
        10,
        5,
        30,
      ),
      passwordMinLength: clampInteger(
        currentSecuritySettings.passwordMinLength,
        8,
        6,
        32,
      ),
      sessionTimeoutMinutes: clampInteger(
        currentSecuritySettings.sessionTimeoutMinutes,
        120,
        1,
        480,
      ),
      requirePasswordChange:
        currentSecuritySettings.requirePasswordChange === true,
    };

    saveSettings({ securitySettings: securityPayload });
  };

  // Helper to update nested securitySettings fields
  const updateSecurityField = (field, value) => {
    updateSettings({
      securitySettings: {
        ...(settings.securitySettings || {}),
        [field]: value,
      },
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Security Settings Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          🔒 Authentication & Session Security
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Configure authentication and session security settings.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "24px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Max Login Attempts
            </label>
            <input
              type="number"
              min="3"
              max="10"
              value={settings.securitySettings?.maxLoginAttempts ?? 5}
              onChange={(e) =>
                updateSecurityField(
                  "maxLoginAttempts",
                  clampInteger(
                    e.target.value,
                    settings.securitySettings?.maxLoginAttempts ?? 5,
                    3,
                    10,
                  ),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p
              style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}
            >
              Number of failed login attempts before account lockout
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Lockout Duration (Minutes)
            </label>
            <input
              type="number"
              min="5"
              max="60"
              value={settings.securitySettings?.lockoutDurationMinutes ?? 15}
              onChange={(e) =>
                updateSecurityField(
                  "lockoutDurationMinutes",
                  clampInteger(
                    e.target.value,
                    settings.securitySettings?.lockoutDurationMinutes ?? 15,
                    5,
                    60,
                  ),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p
              style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}
            >
              How long an account is locked after max login attempts
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              OTP Expiry (Minutes)
            </label>
            <input
              type="number"
              min="5"
              max="30"
              value={settings.securitySettings?.otpExpiryMinutes ?? 10}
              onChange={(e) =>
                updateSecurityField(
                  "otpExpiryMinutes",
                  clampInteger(
                    e.target.value,
                    settings.securitySettings?.otpExpiryMinutes ?? 10,
                    5,
                    30,
                  ),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p
              style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}
            >
              How long one-time passwords remain valid
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Min Password Length
            </label>
            <input
              type="number"
              min="6"
              max="32"
              value={settings.securitySettings?.passwordMinLength ?? 8}
              onChange={(e) =>
                updateSecurityField(
                  "passwordMinLength",
                  clampInteger(
                    e.target.value,
                    settings.securitySettings?.passwordMinLength ?? 8,
                    6,
                    32,
                  ),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p
              style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}
            >
              Minimum required password length for new accounts
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Session Timeout (Minutes)
            </label>
            <input
              type="number"
              min="1"
              max="480"
              value={settings.securitySettings?.sessionTimeoutMinutes ?? 120}
              onChange={(e) =>
                updateSecurityField(
                  "sessionTimeoutMinutes",
                  clampInteger(
                    e.target.value,
                    settings.securitySettings?.sessionTimeoutMinutes ?? 120,
                    1,
                    480,
                  ),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <p
              style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}
            >
              Auto-logout after inactivity (1-480 minutes)
            </p>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              paddingTop: "28px",
            }}
          >
            <input
              type="checkbox"
              id="requirePasswordChange"
              checked={
                settings.securitySettings?.requirePasswordChange === true
              }
              onChange={(e) =>
                updateSecurityField("requirePasswordChange", e.target.checked)
              }
              style={{ width: "20px", height: "20px", cursor: "pointer" }}
            />
            <label
              htmlFor="requirePasswordChange"
              style={{ fontWeight: "500" }}
            >
              Require Periodic Password Changes
            </label>
          </div>
        </div>
      </div>

      {/* Password Policy Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          🔑 Password Policy
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Current password requirements and enforcement.
        </p>

        <div
          style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "20px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "12px",
            }}
          >
            Active Password Requirements:
          </h3>
          <ul
            style={{
              margin: "0",
              paddingLeft: "20px",
              lineHeight: "1.8",
              color: "#374151",
            }}
          >
            <li>
              Minimum length:{" "}
              <strong>
                {settings.securitySettings?.passwordMinLength ?? 8}
              </strong>{" "}
              characters
            </li>
            <li>Must contain at least one uppercase letter (A-Z)</li>
            <li>Must contain at least one lowercase letter (a-z)</li>
            <li>Must contain at least one number (0-9)</li>
            <li>Must contain at least one special character (!@#$%^&*)</li>
            {settings.securitySettings?.requirePasswordChange && (
              <li>
                <strong>Passwords must be changed every 90 days</strong>
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* API & Rate Limiting Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          ⚡ API & Rate Limiting
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Automatic rate limiting protects the platform from abuse and DDoS
          attacks.
        </p>

        <div
          style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "20px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "12px",
            }}
          >
            Current Rate Limits:
          </h3>
          <div style={{ display: "grid", gap: "12px", color: "#374151" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Authentication endpoints:</span>
              <strong>10 requests / 15 minutes</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>API endpoints (authenticated):</span>
              <strong>100 requests / minute</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Public endpoints:</span>
              <strong>50 requests / minute</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Webhook endpoints:</span>
              <strong>1000 requests / minute</strong>
            </div>
          </div>
          <p
            style={{
              marginTop: "16px",
              fontSize: "12px",
              color: "#6b7280",
              fontStyle: "italic",
            }}
          >
            Note: Rate limits are configured in the backend middleware and
            cannot be changed from this interface. Contact system administrator
            to modify.
          </p>
        </div>
      </div>

      {/* System Monitoring Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          📊 System Monitoring
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Overview of security monitoring and logging.
        </p>

        <div
          style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "20px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "12px",
            }}
          >
            Active Monitoring:
          </h3>
          <ul
            style={{
              margin: "0",
              paddingLeft: "20px",
              lineHeight: "1.8",
              color: "#374151",
            }}
          >
            <li>✅ Failed login attempts are logged and tracked</li>
            <li>✅ All authentication events are recorded</li>
            <li>✅ Stripe webhook signatures are verified</li>
            <li>✅ JWT tokens are validated on every request</li>
            <li>✅ CORS is configured for allowed origins only</li>
            <li>✅ Security headers (Helmet) are active</li>
            <li>✅ SQL injection protection via parameterized queries</li>
            <li>✅ XSS protection enabled</li>
          </ul>
        </div>
      </div>

      {/* Save Buttons */}
      <SettingsSaveButtons
        onSave={handleSubmit}
        onCancel={resetSettings}
        saving={saving}
        hasChanges={hasUnsavedChanges}
      />

      {/* Info Box */}
      <div
        style={{
          marginTop: "24px",
          padding: "16px",
          background: "#fef3c7",
          border: "1px solid #fbbf24",
          borderRadius: "8px",
          fontSize: "13px",
          color: "#78350f",
        }}
      >
        <strong>⚠️ Security Best Practices:</strong>
        <ul style={{ margin: "8px 0 0", paddingLeft: "20px" }}>
          <li>
            Keep max login attempts between 3-5 to prevent brute force attacks
          </li>
          <li>Set lockout duration to at least 15 minutes</li>
          <li>Require passwords of at least 8 characters with complexity</li>
          <li>Session timeouts of 2 hours (120 minutes) are recommended</li>
          <li>OTP expiry should be 5-10 minutes for security</li>
        </ul>
      </div>
    </form>
  );
};

export default SecuritySettings;
